import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as AgentSelectors from '../../../store/agents.selectors';
import {
  IAgent,
  IAgentKpi,
  IGroupedOrders,
} from '../../../models/agents.models';
import {
  distinctUntilChanged,
  filter,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { Store } from '@ngrx/store';
import { DATE_FORMAT } from 'src/app/config/global.config';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IQuotation } from 'src/app/features/quotations/models/quotations.models';

@Component({
  selector: 'app-agent-orders',
  templateUrl: './agent-orders.component.html',
  styleUrls: ['./agent-orders.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('expanded', style({ height: '*', opacity: 1, overflow: 'hidden' })),
      state(
        'collapsed',
        style({ height: '0px', opacity: 0, overflow: 'hidden' })
      ),
      transition('expanded <=> collapsed', animate('300ms ease-in-out')),
    ]),
  ],
})
export class AgentOrdersComponent implements OnInit {
  @Input() active_date_filter: 'year_to_date' | 'last_year' = 'year_to_date';
  @Output() goToTab = new EventEmitter<'portfolio' | 'catalogs'>();
  @Output() dateFilterChanged = new EventEmitter<{ start_date: string, end_date: string,  filter: 'year_to_date' | 'last_year' }>();

  agent$: Observable<IAgent | null> = this.store.select(
    AgentSelectors.selectAgent
  );
  kpi: IAgentKpi = {
    total_orders: 0,
    total_orders_change: 0,
    total_orders_amount: 0,
    total_orders_amount_change: 0,
    total_orders_commissions: 0,
    total_orders_commissions_change: 0,
  };

  orders: IQuotation[] = [];
  groupedOrders: IGroupedOrders[] = [];

  date_filter: 'year_to_date' | 'last_year' = 'year_to_date';

  isCurrentMonthExpanded: boolean = true;

  DATE_FORMAT = DATE_FORMAT;

  destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.date_filter = this.active_date_filter;
    this.agent$
      .pipe(
        filter((agent) => agent != null),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((agent: IAgent | null) => {
        if (agent) {
          this.orders = agent.orders!;
          this.kpi = agent.kpi!;

          this._groupAndFormatSales();
        }
      });
  }

  filterByDate(filter: 'year_to_date' | 'last_year') {
    this.date_filter = filter;

    const now = new Date();
    let start_date: string = '';
    let end_date: string = '';

    if (filter === 'year_to_date') {
      // To avoid timezone issues where the date might be shifted to Dec 31st,
      // use setUTCFullYear and setUTCMonth for a guaranteed Jan 1st in UTC.
      const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
      start_date = start.toISOString().slice(0, 10); // Jan 1st of current year (UTC)
      end_date = now.toISOString().slice(0, 10); // today
    } else if (filter === 'last_year') {
      // To avoid timezone issues for last year, use UTC for both start and end dates
      const lastYear = now.getUTCFullYear() - 1;
      const start = new Date(Date.UTC(lastYear, 0, 1)); // Jan 1st of last year (UTC)
      const end = new Date(Date.UTC(lastYear, 11, 31)); // Dec 31st of last year (UTC)
      start_date = start.toISOString().slice(0, 10);
      end_date = end.toISOString().slice(0, 10);
    }

    this.dateFilterChanged.emit({ start_date, end_date, filter });
  }

  toggleExpand(group: IGroupedOrders) {
    group.expanded = !group.expanded;
  }

  getStatusTranslation(status: string): string {
    return (
      this.translateService.instant(
        `quotations_and_orders.QuotationStatus.${status}`
      ) || status
    );
  }

  getStatusStyles(status: string): { [key: string]: string } {
    let textColor: string = '';
    let backgroundColor: string = '';
    let borderColor: string = '';

    switch (status) {
      case 'all':
        backgroundColor = '#949494';
        textColor = '#FFFFFF';
        break;
      case 'sent_to_erp':
        backgroundColor = '#DCFFF1';
        textColor = '#174B35';
        break;
      case 'done':
        backgroundColor = '#DCFFF1';
        textColor = '#174B35';
        break;
      case 'pending_inventory':
        backgroundColor = '#FFE489';
        textColor = '#785500';
        break;
      case 'sale':
        backgroundColor = '#D4FAE6';
        textColor = '#529C7B';
        break;
      case 'cancel':
        backgroundColor = '#FBDDD8';
        textColor = '#970000';
        break;
      case 'declined':
        backgroundColor = '#FBDDD8';
        textColor = '#970000';
        break;
      case 'draft':
        backgroundColor = '#E2E2E2';
        textColor = '#595959';
        break;
      case 'deleted':
        backgroundColor = '#FBDDD8';
        textColor = '#970000';
        break;
      default:
        backgroundColor = '#E9F2FF';
        textColor = '#6449DB';
        break;
    }

    return {
      'background-color': backgroundColor,
      color: textColor,
      'border-color': borderColor,
    };
  }

  goToOrder(order: IQuotation) {
    this.router.navigate(['/quotations/', order.id]);
  }

  switchToTab(tab: 'portfolio' | 'catalogs'): void {
    this.goToTab.emit(tab);
  }

  _groupAndFormatSales() {
    if (!this.orders || !this.orders.length) {
      this.groupedOrders = [];
      return;
    }

    // Sort data descending by date_order
    const sortedOrders = [...this.orders].sort(
      (a, b) =>
        new Date(b.date_order).getTime() - new Date(a.date_order).getTime()
    );

    // Group by month
    const groupedMap: { [key: string]: IGroupedOrders } = {};

    sortedOrders.forEach((order) => {
      const monthKey = order.date_order.slice(0, 7); // "YYYY-MM"

      if (!groupedMap[monthKey]) {
        groupedMap[monthKey] = {
          month: monthKey,
          expanded: true,
          displayMonth: '',
          orders: [],
        };
      }
      groupedMap[monthKey].orders.push(order);
    });

    // Convert grouped object to array and sort descending by month
    let groupedArray = Object.values(groupedMap).sort((a, b) => {
      return (
        new Date(b.month + '-01').getTime() -
        new Date(a.month + '-01').getTime()
      );
    });

    // Format displayMonth
    const now = new Date();
    const currentMonth = now.toISOString().slice(0, 7); // "YYYY-MM"
    const locale = this.translateService.currentLang || 'en';

    groupedArray = groupedArray.map((group) => {
      const date = new Date(group.month + '-01');
      const displayMonth =
        group.month === currentMonth
          ? this.translateService.instant(
              'agent.single_agent.orders.current_month'
            ) || 'This month'
          : date.toLocaleString(locale, { month: 'long', year: 'numeric' });

      return {
        ...group,
        displayMonth,
      };
    });

    this.groupedOrders = groupedArray;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
